import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../css/verificationQueue.module.scss";
import skeletonStyles from "assets/scss/common/viewSkeleton.module.scss";
import { useVerificationService } from "../api/useVerificationQuery";
import { QUEUE_MODULE_CONFIGS, getBadgeConfig } from "../helper/verification.constants";
import { showToastMessage, GetUserData, formatDate } from "common/service/CommonService";
import SimpleBar from "simplebar-react";
import VerificationDetailModal from "../components/VerificationDetailModal";
import PinVerificationModal from "../../Pharmacy/DailyReconsilation/components/PinVerificationModal";
import { useBlockVolunteerService } from "../../Volunteer/BlockVolunteer/api/useBlockVolunteerQuery";
import { useDropoutVolunteerService } from "../../Volunteer/DropoutVolunteer/api/useDropoutVolunteerQuery";
import { useTransferVolunteerService } from "../../Volunteer/TransferVolunteer/api/useTransferVolunteerQuery";
import { useIpInventoryService } from "../../Pharmacy/IPinventory/api/useIpInventoryService";
import { useRetentionService } from "../../Pharmacy/Retention/api/useRetentionService";
import { useDispensingLog } from "../../Pharmacy/Dispensing/api/useDispensingLog";
import { useReturnsAccountability } from "../../Pharmacy/ReturnsLog/api/useReturnsAccountability";
import { useTransferService as usePharmacyTransferService } from "../../Pharmacy/Transfer/api/useTransferService";
import { useDisposalService } from "../../Pharmacy/Disposal/api/useDisposal";
import { usePharmacyLedgerService } from "../../Pharmacy/DailyReconsilation/api/usePharmacyLedger";
import { useAdverseEventApi } from "../../Library/AdverseEvent/api/useAdverseEventApi";
import { useLabTestsService } from "../../Volunteer/AssigningStudyModule/StudyTestList/api/useStudyScreeningTests";
import VerifyLabTestModal from "../../Volunteer/AssigningStudyModule/StudyTestList/components/VerifyLabTestModal";
import { useVerifyDosingSchedule } from "../../Clinical/DosingSchedule/api/useDosingScheduleHook";
import { queryClient } from "api/queryClient";
import { useGlobalStudy } from "context/StudyContext";
import CommonOffcanvasLayout from "Components/Common/CommonOffcanvasLayout";
import { Button } from "reactstrap";
import { ACCESSIONING_PATHS, VERIFICATION_PATHS } from "Routes/paths";
import { usePermission } from "context/PermissionContext";
import { PERMISSION_MODULES, PERMISSION_ACTIONS } from "common/constants/PermissionConstants";

const getInitials = (name: string) => {
  if (!name) return "?";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

interface VerificationQueueProps {
  isOpen?: boolean;
  toggle?: () => void;
}

// Definitions extracted to src/modules/Verification/helper/verification.constants.ts

const VerificationQueue: React.FC<VerificationQueueProps> = ({ isOpen = true, toggle }) => {
  const navigate = useNavigate();
  const { selectedStudy: globalStudy } = useGlobalStudy();
  const { hasPermission } = usePermission();

  // Call verification SelectAll service with global study id
  const { SelectAllQuery } = useVerificationService({
    studyid: Number(globalStudy?.studyid),
    enabled: isOpen,
  });

  // Instantiate verify mutations using existing services
  const { VerifyMutation: VerifyBlockMutation } = useBlockVolunteerService({ enableSelectAll: false });
  const { VerifyMutation: VerifyDropoutMutation } = useDropoutVolunteerService({ enableSelectAll: false });
  const { VerifyMutation: VerifyTransferMutation } = useTransferVolunteerService({ enableSelectAll: false });
  const { verifyPinMutation: VerifyIpInventoryMutation } = useIpInventoryService();
  const { verifyPinMutation: VerifyRetentionMutation } = useRetentionService();
  const { verifyPinMutation: VerifyDispensingMutation } = useDispensingLog();
  const { verifyPinMutation: VerifyReturnsLogMutation } = useReturnsAccountability();
  const { verifyPinMutation: VerifyDrugTransferMutation } = usePharmacyTransferService();
  const { verifyPinMutation: VerifyDisposalMutation } = useDisposalService();
  const { verifyPinMutation: VerifyReconciliationMutation } = usePharmacyLedgerService();
  const { VerifyMutation: VerifyAdverseEventMutation } = useAdverseEventApi();
  const { VerifyLabTestMutation } = useLabTestsService({});
  const { verifyPinMutation: VerifyDosingScheduleMutation } = useVerifyDosingSchedule();

  // State Management
  const [activeRecord, setActiveRecord] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isPinModalOpen, setIsPinModalOpen] = useState<boolean>(false);
  const [isPinInvalid, setIsPinInvalid] = useState<boolean>(false);

  const userData = GetUserData();

  const mutations: Record<string, any> = {
    BLOCK: VerifyBlockMutation,
    DROPOUT: VerifyDropoutMutation,
    TRANSFER: VerifyTransferMutation,
    IP_INVENTORY: VerifyIpInventoryMutation,
    RETENTION: VerifyRetentionMutation,
    DISPENSING: VerifyDispensingMutation,
    RETURNS_LOG: VerifyReturnsLogMutation,
    DRUG_TRANSFER: VerifyDrugTransferMutation,
    DISPOSAL: VerifyDisposalMutation,
    RECONCILIATION: VerifyReconciliationMutation,
    ADVERSE_EVENT: VerifyAdverseEventMutation,
    LAB_TEST: VerifyLabTestMutation,
    DOSING_SCHEDULE: VerifyDosingScheduleMutation,
  };

  const isVerifyPending = activeRecord ? mutations[activeRecord.moduletype]?.isPending : false;

  // Combine & Sort Data (BLOCK + DROPOUT + TRANSFER + IP-INVENTORY etc.)
  const combinedList = useMemo(() => {
    const rawData = SelectAllQuery?.data?.data || {};
    const blocks = hasPermission(PERMISSION_MODULES.BLOCKED_VOLUNTEER, PERMISSION_ACTIONS.VERIFY) ? (rawData.BLOCK || []) : [];
    const dropouts = hasPermission(PERMISSION_MODULES.DROPOUT_VOLUNTEER, PERMISSION_ACTIONS.VERIFY) ? (rawData.DROPOUT || []) : [];
    const transfers = hasPermission(PERMISSION_MODULES.TRANSFER_VOLUNTEER, PERMISSION_ACTIONS.VERIFY) ? (rawData.TRANSFER || []) : [];
    const ipInventories = hasPermission(PERMISSION_MODULES.IP_INVENTORY, PERMISSION_ACTIONS.VERIFY) ? (rawData.IP_INVENTORY || []) : [];
    const retentions = hasPermission(PERMISSION_MODULES.DRUG_RETENTION, PERMISSION_ACTIONS.VERIFY) ? (rawData.RETENTION || []) : [];
    const dispensings = hasPermission(PERMISSION_MODULES.DISPENSING_LOG, PERMISSION_ACTIONS.VERIFY) ? (rawData.DISPENSING || []) : [];
    const returnsLogs = hasPermission(PERMISSION_MODULES.RETURNS_LOG, PERMISSION_ACTIONS.VERIFY) ? (rawData.RETURNS_LOG || []) : [];
    const drugTransfers = hasPermission(PERMISSION_MODULES.DRUG_TRANSFER, PERMISSION_ACTIONS.VERIFY) ? (rawData.DRUG_TRANSFER || []) : [];
    const disposals = hasPermission(PERMISSION_MODULES.DRUG_DISPOSAL, PERMISSION_ACTIONS.VERIFY) ? (rawData.DISPOSAL || []) : [];
    const reconciliations = hasPermission(PERMISSION_MODULES.STOCK_RECONCILIATION, PERMISSION_ACTIONS.VERIFY) ? (rawData.RECONCILIATION || []) : [];
    const adverseEvents = hasPermission(PERMISSION_MODULES.ADVERSE_EVENT, PERMISSION_ACTIONS.VERIFY) ? (rawData.ADVERSE_EVENT || []) : [];
    const ambulatoryVisits = hasPermission(PERMISSION_MODULES.AMBULATORY_VISIT, PERMISSION_ACTIONS.VERIFY) ? (rawData.AMBULATORY_VISIT || []) : [];
    const labTests = hasPermission(`${PERMISSION_MODULES.STUDY_SCREENING}:LABTEST`, PERMISSION_ACTIONS.VERIFY) ? (rawData.LAB_TEST || []) : [];
    const sampleCollections = rawData.SAMPLE_COLLECTION || [];
    const dosingSchedules = hasPermission(PERMISSION_MODULES.DOSING_SCHEDULE, PERMISSION_ACTIONS.VIEW) ? (rawData.DOSING_SCHEDULE || []) : [];

    // Combine list and sort descending by verificationqueueid
    return [
      ...blocks,
      ...dropouts,
      ...transfers,
      ...ipInventories,
      ...retentions,
      ...dispensings,
      ...returnsLogs,
      ...drugTransfers,
      ...disposals,
      ...reconciliations,
      ...adverseEvents,
      ...ambulatoryVisits,
      ...labTests,
      ...sampleCollections,
      ...dosingSchedules,
    ].sort((a, b) => b.verificationqueueid - a.verificationqueueid);
  }, [SelectAllQuery?.data?.data, hasPermission]);

  const handleVerifyClick = (record: any) => {
    setIsPinModalOpen(true);
    setIsPinInvalid(false);
  };

  const handleActionClick = (action: string, record: any) => {
    showToastMessage(
      "success",
      `Action "${action}" processed locally for request ID: ${record?.verificationqueueid}`
    );
  };

  const handlePinVerify = (password: string, decision?: "Pass" | "Fail") => {
    if (!activeRecord) return;
    setIsPinInvalid(false);

    const { moduletype, recordid } = activeRecord;
    const userId = Number(userData?.userid);

    const config = QUEUE_MODULE_CONFIGS[moduletype];
    if (!config) {
      showToastMessage("error", `Unsupported module type: ${moduletype}`);
      return;
    }

    const mutation = mutations[moduletype];
    const moduleQueryKey = config.queryKey;

    const payloadParams: any = {
      [config.idParamName]: Number(recordid),
    };

    const isIpModule = [
      "IP_INVENTORY",
      "RETENTION",
      "DISPENSING",
      "RETURNS_LOG",
      "DRUG_TRANSFER",
      "DISPOSAL",
      "RECONCILIATION",
      "AMBULATORY_VISIT",
      "LAB_TEST",
      "DOSING_SCHEDULE"
    ].includes(moduletype);

    if (isIpModule) {
      payloadParams.studyid = Number(activeRecord.recorddetail?.studyid);
    }

    if (moduletype === "DOSING_SCHEDULE") {
      payloadParams.subjectsperstation = activeRecord.recorddetail?.subjectsperstation || {};
    }

    if (moduletype === "LAB_TEST") {
      payloadParams.volunteerid = Number(activeRecord.recorddetail?.volunteerid);
      payloadParams.verifystatus = decision || "Pass";
    }

    const payload = {
      verifypin: password,
      verifiedby: userId,
      ...payloadParams,
    };

    mutation.mutate(payload, {
      onSuccess: (response: any) => {
        queryClient.invalidateQueries({ queryKey: ["verification-queue-select-all"] });
        queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] }); // Update header notification count immediately
        if (moduleQueryKey) {
          queryClient.invalidateQueries({ queryKey: [moduleQueryKey] });
        }
        showToastMessage("success", response?.message || "Record verified successfully");
        setIsPinModalOpen(false);
        setIsModalOpen(false);
        setActiveRecord(null);
      },
      onError: (error: any) => {
        const errorMsg = typeof error === "string" ? error : error?.message ?? "";
        if (errorMsg.toLowerCase().includes("incorrect password")) {
          setIsPinInvalid(true);
        } else {
          setIsPinInvalid(false);
        }
        showToastMessage("error", errorMsg || "Failed to verify record");
      },
    });
  };

  // Badge config helper (imported from verification.constants.ts)

  return (
    <>
      <CommonOffcanvasLayout
        isOpen={isOpen}
        toggle={toggle ? toggle : () => { }}
        title="Message Center"
        width="450px"
      >
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
          {SelectAllQuery.isFetching ? (
            <div className={styles.cardList} style={{ gridTemplateColumns: "1fr", padding: 0 }}>
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className={styles.notificationCard} style={{ pointerEvents: "none" }}>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div className={`${skeletonStyles.skeleton} ${styles.skeletonAvatar}`} />
                    <div className="d-flex flex-column" style={{ flexGrow: 1 }}>
                      <div className={`${skeletonStyles.skeleton} ${styles.skeletonTitle}`} />
                      <div className={`${skeletonStyles.skeleton} ${styles.skeletonSubtitle}`} />
                    </div>
                    <div className="d-flex align-items-center gap-1 ms-auto">
                      <div className={`${skeletonStyles.skeleton} ${styles.skeletonBadge}`} />
                      <div className={`${skeletonStyles.skeleton} ${styles.skeletonEye}`} />
                    </div>
                  </div>
                  <div>
                    <div className={`${skeletonStyles.skeleton} ${styles.skeletonText1}`} />
                    <div className={`${skeletonStyles.skeleton} ${styles.skeletonText2}`} />
                  </div>
                </div>
              ))}
            </div>
          ) : combinedList.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>
                <i className="ri-inbox-line" />
              </div>
              <p className={styles.emptyTitle}>No messages found</p>
              <p className={styles.emptyText}>There are no pending verification requests.</p>
            </div>
          ) : (
            <SimpleBar style={{ maxHeight: "calc(100vh - 120px)", flex: 1 }} className="pb-3">
              <div className={styles.cardList} style={{ gridTemplateColumns: "1fr", padding: "4px" }}>
                {combinedList.map((record) => {
                  const badge = getBadgeConfig(record.moduletype, true);
                  const isActive = isModalOpen && activeRecord?.verificationqueueid === record.verificationqueueid;
                  const creatorInitials = getInitials(record.createdbyname);

                  const config = QUEUE_MODULE_CONFIGS[record.moduletype] || {
                    label: record.moduletype,
                    badgeIcon: "ri-notification-3-line",
                    badgeClass: "",
                    mainMessage: "Verification request has been initiated.",
                    getSubMessage: () => "Please verify the details of this request.",
                  };

                  const mainMsg = config.mainMessage;
                  const subMsg = config.getSubMessage(record.recorddetail);

                  return (
                    <div
                      key={record.verificationqueueid}
                      className={`${styles.notificationCard} ${isActive ? styles.activeCard : ""}`}
                    >
                      {/* Profile Section */}
                      <div className="d-flex align-items-center gap-1 mb-3">
                        <div className={styles.cardAvatar}>
                          {creatorInitials}
                        </div>
                        <div className="d-flex flex-column">
                          <span className={styles.creatorName}>
                            {record.createdbyname || "System User"}
                          </span>
                          <span className={styles.creatorDate}>
                            {formatDate(record.createddate)}
                          </span>
                        </div>

                        {/* Module Badge & Eye Icon on the right (hidden for SAMPLE_COLLECTION) */}
                        <div className="ms-auto d-flex align-items-center gap-2">
                          <span className={`${styles.typeBadge} ${badge.className} ${styles.compactBadge}`}>
                            <i className={badge.icon} style={{ marginRight: "2px" }} />
                            {badge.label}
                          </span>
                          {record.moduletype !== "SAMPLE_COLLECTION" && (
                            <button
                              type="button"
                              className={`btn ${styles.eyeViewBtn}`}
                              title="View Details"
                              onClick={() => {
                                setActiveRecord(record);
                                setIsModalOpen(true);
                              }}
                            >
                              <i className="ri-eye-line" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Content Section */}
                      <div className="mb-1">
                        <p className={styles.mainMessage}>
                          {mainMsg}
                        </p>
                        <p className={styles.subMessage}>
                          {subMsg}
                        </p>
                      </div>

                      {/* Actions Section */}
                      <div className={styles.cardActions}>
                        {record.moduletype === "SAMPLE_COLLECTION" ? (
                          <Button
                            color="primary"
                            outline
                            style={{ padding: "2px 10px", fontSize: "12px" }}
                            onClick={() => {
                              const periodId = record.recorddetail?.periodid;
                              const subjectNo = record.subjectno || record.recorddetail?.subjectno;
                              const path = VERIFICATION_PATHS.SAMPLE_COLLECTION.replace(
                                ":periodid",
                                String(periodId ?? "0")
                              );
                              navigate(path, { state: { periodid: periodId, subjectno: subjectNo } });
                              if (toggle) toggle();
                            }}
                          >
                            <i className="ri-eye-line me-1" />
                            View
                          </Button>
                        ) : (
                          <>
                            <Button 
                              color="success" 
                              outline
                              style={{padding:"2px 10px", fontSize:"12px"}}
                              onClick={() => {
                                setActiveRecord(record);
                                handleVerifyClick(record);
                              }}
                            >
                              Verify
                            </Button>
                            <Button 
                              color="danger" 
                              outline
                              style={{padding:"2px 10px", fontSize:"12px"}}
                              onClick={() => {
                                setActiveRecord(record);
                                handleActionClick("Reject", record);
                              }}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </SimpleBar>
          )}
        </div>
      </CommonOffcanvasLayout>

      {/* Verification Modals */}
      <VerificationDetailModal
        isOpen={isModalOpen}
        toggle={() => setIsModalOpen(false)}
        record={activeRecord}
      />

      <PinVerificationModal
        isOpen={isPinModalOpen && activeRecord?.moduletype !== "LAB_TEST"}
        toggle={() => setIsPinModalOpen(false)}
        onVerify={handlePinVerify}
        isLoading={isVerifyPending}
        title="Verify Action Record"
        description={
          <>
            Please enter your password to verify this action for{" "}
            {activeRecord?.moduletype === "ADVERSE_EVENT" ? (
              <>
                Adverse Event: <strong>{activeRecord?.recorddetail?.eventterm || "-"}</strong>
              </>
            ) : activeRecord?.moduletype === "IP_INVENTORY" || activeRecord?.moduletype === "RETENTION" || activeRecord?.moduletype === "DISPENSING" || activeRecord?.moduletype === "RETURNS_LOG" || activeRecord?.moduletype === "DRUG_TRANSFER" || activeRecord?.moduletype === "DISPOSAL" || activeRecord?.moduletype === "RECONCILIATION" ? (
              <>
                IP Product: <strong>{activeRecord?.recorddetail?.productname || "-"}</strong>
              </>
            ) : activeRecord?.moduletype === "AMBULATORY_VISIT" ? (
              <>
                Ambulatory Visit: <strong>{activeRecord?.recorddetail?.visitname || activeRecord?.recorddetail?.visitnumber || "-"}</strong>
              </>
            ) : (
              <>
                volunteer: <strong>{activeRecord?.recorddetail?.initials || activeRecord?.recorddetail?.fullname || "-"}</strong>
              </>
            )}
          </>
        }
        isInvalid={isPinInvalid}
      />

      <VerifyLabTestModal
        isOpen={isPinModalOpen && activeRecord?.moduletype === "LAB_TEST"}
        toggle={() => setIsPinModalOpen(false)}
        onVerify={(password: string, decision: "Pass" | "Fail") => {
          handlePinVerify(password, decision);
        }}
        isLoading={isVerifyPending}
        title="Verify Lab Test Record"
        recordName={activeRecord?.recorddetail?.reportname}
        isInvalid={isPinInvalid}
      />
    </>
  );
};

export default VerificationQueue;
